import { useMemo, useState } from "react";
import { articles } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";

const tabs = ["全部", "影评", "资讯"];
const PAGE_SIZE = 6;

export default function NewsList() {
  usePageMeta(
    "影视影评与资讯 - 80s电影",
    "80s电影收录八十年代经典影片的深度影评与影视资讯，从创作背景到镜头语言，为影迷提供更长线的阅读内容。",
    "80年代电影影评,影视资讯,经典电影评论,80s电影,怀旧电影"
  );

  const [tab, setTab] = useState("全部");
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => [...articles].sort((a, b) => b.date.localeCompare(a.date)), []);
  const filtered = useMemo(
    () => (tab === "全部" ? sorted : sorted.filter((article) => article.type === tab)),
    [sorted, tab]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const switchTab = (next: string) => {
    setTab(next);
    setPage(1);
  };

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-page px-4 py-9 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影评资讯" }]} />
          <h1 className="mt-5 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
            影视影评与资讯
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            我们相信影评不是评分，而是一次认真对话。这里的每一篇文章都尽量交代清楚背景、方法与立场：既不把怀旧当作滤镜，也不为了唱反调而唱反调。你可以顺着文章里的影片链接，回到资料页继续读。
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-xs text-secondary-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-quill-pen-line text-primary-600"></i>
              影评 {articles.filter((item) => item.type === "影评").length} 篇
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-newspaper-line text-primary-600"></i>
              资讯 {articles.filter((item) => item.type === "资讯").length} 篇
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-primary-600"></i>
              更新至 2026 年 9 月
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-9 md:px-6 md:py-12">
        <div className="flex flex-col gap-4 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {tabs.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => switchTab(item)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  tab === item
                    ? "bg-primary-700 text-background-50"
                    : "text-foreground-700 hover:text-primary-700"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
          <p className="font-label text-sm uppercase tracking-[0.16em] text-secondary-600">
            共 <span className="text-primary-700">{filtered.length}</span> 篇文章
          </p>
        </div>

        <div className="mt-8">
          {visible.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
              <Pagination page={safePage} totalPages={totalPages} onPageChange={setPage} />
            </>
          ) : (
            <EmptyState
              icon="ri-article-line"
              title="暂无相关文章内容"
              description="这个分类下暂时还没有文章。你可以切换到「全部」查看已有的影评与资讯。"
              action={
                <button
                  type="button"
                  onClick={() => switchTab("全部")}
                  className="rounded-md bg-primary-700 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-800"
                >
                  查看全部文章
                </button>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}